import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  getDocs,
  where,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Card,
  Tooltip,
  Avatar,
  Chip,
  Grid,
} from "@mui/material";
import { format, subDays, formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { sv } from "date-fns/locale";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const StatsCard = ({ title, value, subtitle, color = "primary" }) => (
  <Card
    sx={{
      p: 2,
      height: "100%",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }}>
      {title}
    </Typography>
    <Typography variant="h3" sx={{ fontWeight: 700, my: 1 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="body2" sx={{ opacity: 0.8 }}>
        {subtitle}
      </Typography>
    )}
  </Card>
);

const DailyActivityChart = ({ user }) => {
  const [dailyStats, setDailyStats] = useState([]);

  useEffect(() => {
    const fetchDailyStats = async () => {
      try {
        const fourteenDaysAgo = subDays(new Date(), 14);

        const usersQuery = query(
          collection(db, "users"),
          where("lastLogin", ">=", Timestamp.fromDate(fourteenDaysAgo)),
          orderBy("lastLogin", "desc"),
        );

        const snapshot = await getDocs(usersQuery);
        const userData = snapshot.docs.map((doc) => ({
          lastLogin: doc.data().lastLogin?.toDate(),
        }));

        // Group by date
        const dailyCounts = {};
        userData.forEach((user) => {
          if (user.lastLogin) {
            const dateStr = format(user.lastLogin, "yyyy-MM-dd");
            dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
          }
        });

        // Create array of last 14 days
        const stats = Array.from({ length: 14 }, (_, i) => {
          const date = subDays(new Date(), i);
          const dateStr = format(date, "yyyy-MM-dd");
          return {
            date: format(date, "dd/MM", { locale: sv }),
            fullDate: dateStr,
            count: dailyCounts[dateStr] || 0,
          };
        }).reverse();

        setDailyStats(stats);
      } catch (error) {
        console.error("Error fetching daily stats:", error);
      }
    };

    fetchDailyStats();
  }, []);

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Daglig aktivitet (14 dagar)
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={dailyStats}>
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
          />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
          <Bar dataKey="count" fill="#667eea" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const AdminUserDashboard = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user.admin) {
      navigate("/");
    }
  }, [navigate]);

  const adminUsers = ["Z6BLSMsm38Un5mgW0O6Z3TToHq12"];

  const isValidDate = (date) => {
    return (
      date &&
      Object.prototype.toString.call(date) === "[object Date]" &&
      !isNaN(date)
    );
  };

  const getInitials = (email) => {
    if (!email) return "?";
    const name = email.split("@")[0];
    return name.charAt(0).toUpperCase();
  };

  const getAccountTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "premium":
        return "success";
      case "pro":
        return "warning";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const fourDaysAgo = subDays(new Date(), 4);

        const usersQuery = query(
          collection(db, "users"),
          where("lastLogin", ">=", Timestamp.fromDate(fourDaysAgo)),
          orderBy("lastLogin", "desc"),
        );

        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            let createdAt = data.createdAt;
            if (
              createdAt &&
              typeof createdAt === "object" &&
              "seconds" in createdAt
            ) {
              createdAt = createdAt.toDate();
            } else if (createdAt && typeof createdAt.toDate === "function") {
              createdAt = createdAt.toDate();
            } else if (typeof createdAt === "string") {
              createdAt = new Date(createdAt);
            }

            return {
              id: doc.id,
              ...data,
              lastLogin: data.lastLogin?.toDate(),
              createdAt: createdAt,
            };
          })
          .filter((user) => !adminUsers.includes(user.id));

        setUsers(usersData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching users:", error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const formatDate = (date) => {
    if (!isValidDate(date)) return "-";
    try {
      return format(date, "dd/MM HH:mm", { locale: sv });
    } catch (error) {
      console.error("Error formatting date:", date, error);
      return "-";
    }
  };

  const formatAccountAge = (date) => {
    if (!isValidDate(date)) return "-";
    try {
      return formatDistanceToNow(date, { locale: sv, addSuffix: true });
    } catch (error) {
      console.error("Error formatting account age:", date, error);
      return "-";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Calculate stats
  const totalLogins = users.reduce(
    (sum, user) => sum + (user.loginCountNew || 0),
    0,
  );
  const newUsersToday = users.filter((user) => {
    if (!user.createdAt) return false;
    const today = new Date();
    const userDate = new Date(user.createdAt);
    return userDate.toDateString() === today.toDateString();
  }).length;

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: "1400px",
        margin: "0 auto",
        bgcolor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        sx={{ mb: 4, fontWeight: 700, color: "#1e293b" }}
      >
        Användaraktivitet
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Aktiva användare"
            value={users.length}
            subtitle="Senaste 4 dagarna"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2,
              height: "100%",
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }}>
              Totala inloggningar
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, my: 1 }}>
              {totalLogins}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2,
              height: "100%",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }}>
              Nya idag
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, my: 1 }}>
              {newUsersToday}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2,
              height: "100%",
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }}>
              Genomsnitt/användare
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, my: 1 }}>
              {users.length ? Math.round(totalLogins / users.length) : 0}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              inloggningar
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <DailyActivityChart user={user} />

      <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 3, borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
            Användarlista
          </Typography>
        </Box>

        <TableContainer>
          <Table sx={{ "& .MuiTableCell-root": { py: 2 } }}>
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Användare</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Registrerad</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  Senaste inloggning
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Inloggningar</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Skola</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kommun</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{
                    "&:hover": { bgcolor: "#f1f5f9" },
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={user.photoURL}
                        alt={user.email}
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: "1rem",
                        }}
                      >
                        {!user.photoURL && getInitials(user.email)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isValidDate(user.createdAt) ? (
                      <Tooltip title={formatDate(user.createdAt)}>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          {formatAccountAge(user.createdAt)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      {user.lastLogin ? formatDate(user.lastLogin) : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.loginCountNew || 0}
                      size="small"
                      color={
                        user.loginCountNew > 10
                          ? "success"
                          : user.loginCountNew > 5
                            ? "warning"
                            : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {user.accountType ? (
                      <Chip
                        label={user.accountType}
                        size="small"
                        color={getAccountTypeColor(user.accountType)}
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      {user.school || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      {user.municipality || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default AdminUserDashboard;
